import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD_PX = 150;

type UseChatAutoScrollOptions = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  messages: unknown[];
  isLoading: boolean;
};

export function useChatAutoScroll({
  scrollContainerRef,
  messagesEndRef,
  messages,
  isLoading,
}: UseChatAutoScrollOptions) {
  const [stickToBottom, setStickToBottom] = useState(true);
  const lastScrollTopRef = useRef(0);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      // Scroll the transcript directly — `scrollIntoView` also scrolls ancestors and hides the chat header.
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
        return;
      }

      messagesEndRef.current?.scrollIntoView({ behavior });
    },
    [messagesEndRef, scrollContainerRef],
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    lastScrollTopRef.current = container.scrollTop;

    const handleScroll = () => {
      const scrolledUp = container.scrollTop < lastScrollTopRef.current;
      lastScrollTopRef.current = container.scrollTop;

      // Scrolling up is always the user, so stop auto-follow right away.
      if (scrolledUp) {
        setStickToBottom(false);
        return;
      }

      // Re-engage auto-follow only once the user scrolls back near the bottom.
      const distanceFromBottom = container.scrollHeight - container.clientHeight - container.scrollTop;
      if (distanceFromBottom <= BOTTOM_THRESHOLD_PX) {
        setStickToBottom(true);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef]);

  useEffect(() => {
    if (!stickToBottom) return;
    scrollToBottom(isLoading ? 'auto' : 'smooth');
  }, [messages, isLoading, stickToBottom, scrollToBottom]);

  const resetStickToBottom = useCallback(() => {
    setStickToBottom(true);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  const resumeStickToBottom = useCallback(() => {
    setStickToBottom(true);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  return {
    stickToBottom,
    resetStickToBottom,
    resumeStickToBottom,
  };
}
